import DropdownSelector from '@/components/dropdown-selector/DropdownSelector';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import Commands from '../../../../constants/commands';
import { TYPE_ENTRY_ONLY } from '../../../../constants/install-device';
import { NETWORK_MODE } from '../../../../constants/network-constants';
import { HardwareSubCmds, MQTTHardwareSubCmds } from '../../../../constants/sub-commands';
import { useLocale } from '../../../../contexts/localeContext';
import { TableContextProvider } from '../../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../../contexts/TranslationKeys';
import InstalledIcon from '../../../../images/circle-checked.svg';
import DiscoveredIcon from '../../../../images/circle-plus.svg';
import columns from '../../../../models/ColumnDefinitions/DiscoveryColumns';
import { Controller } from '../../../../models/Controller';
import { ExecuteCommand } from '../../../../models/ExecuteCommand';
import { Filter } from '../../../../models/Filter';
import { ListData } from '../../../../models/ListData';
import { ListQuery } from '../../../../models/ListQuery';
import { MultiSelectOption } from '../../../../models/MultiSelectOption';
import searchColumns from '../../../../models/search/DiscoverySearch';
import { TableLegendItem } from '../../../../models/table/TableLegendItem';
import { useGlobalState } from '../../../../Providers';
import { getDiscoveries } from '../../../../services/discoveryService';
import { executeCommand } from '../../../../services/executeCommandService';
import { getSystemUserIpRanges } from '../../../../services/userPreferenceService';
import { getErrorMessage } from '../../../../services/utilities/ServiceError';
import { IAppState } from '../../../../store/types';
import { appendFilter } from '../../../../utilities/filters';
import ActivityFrame from '../../../activity-frame/activity-frame';
import { useDialog } from '../../../dialog/DialogContext';
import TableTab from '../../../tabs/table-tab';
import Tooltip from '../../../Tooltip/Tooltip';
import { AddIpRangeModal } from '../ipAddressWizard/AddIpRangeModal';
import IPRanges from '../ipAddressWizard/IPRanges';
import { SingleIPSearchModal } from '../ipAddressWizard/SingleIPSearchModal';

interface SelectControllerProps {
    selectedDevice: Controller;
    setSelectedDevice: (device: any) => void;
    onNext: (e) => void;
    onBack?: (e) => void;
    filterInstalled?: boolean;
    showLegend?: boolean;
    title: string;
}

const SelectController: FC<SelectControllerProps> = ({ onNext, onBack = undefined, setSelectedDevice, selectedDevice, filterInstalled, showLegend = true, title }) => {
    const { translate } = useLocale();
    const GlobalContext = useGlobalState();
    const isTlsEnabled = GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.CLOUD || GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.LAN;
    const [isNextEnabled, setIsNextEnabled] = useState(false)
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [busy, setBusy] = useState(false);
    const [openAddRangeModal, setOpenAddRangeModal] = useState<boolean>(false);
    const [openIPRangeSelection, setOpenIPRangeSelection] = useState<boolean>(false);
    const [openIPSearch, setOpenIPSearch] = useState<boolean>(false);
    const [advanceScanOptions, setAdvanceScanOptions] = useState<MultiSelectOption[]>([]);
    const commands = {
        SELECT_RANGE: '0',
        ADD_RANGE: '1',
        SPECIFIC_IP: '2'
    }
    const dialog = useDialog();

    useEffect(() => {
        getSystemUserIpRanges().then((ranges) => {
            if (ranges.rows.length > 0) {
                setAdvanceScanOptions([
                    { value: commands.SELECT_RANGE, label: translate.byKey(TranslationKeys.selectRange) },
                    { value: commands.ADD_RANGE, label: translate.byKey(TranslationKeys.addRange) },
                    { value: commands.SPECIFIC_IP, label: translate.byKey(TranslationKeys.specificIP) }
                ]);

                return;
            }

            setAdvanceScanOptions([
                { value: commands.ADD_RANGE, label: translate.byKey(TranslationKeys.addRange) },
                { value: commands.SPECIFIC_IP, label: translate.byKey(TranslationKeys.specificIP) }
            ]);
        }).catch(() => {

        })
    }, [openAddRangeModal, openIPRangeSelection])

    const onRowClicked = (rowData: any) => {
        rowData ? setIsNextEnabled(true) : setIsNextEnabled(false);

        const newSelectedDevice = {
            ...selectedDevice,
            id: rowData.id,
            name: rowData.nameOriginal,
            fixedAddress: rowData.fixedAddress,
            ipAddress: rowData.ipAddress,
            macAddress: rowData.macAddress,
            typeId: rowData.unitTypeId,
            typeName: rowData.unitTypeName,
        } as Controller
        setSelectedDevice(newSelectedDevice);
    }

    const getFilters = (): Filter[] => {
        if (filterInstalled && !!filterInstalled) {
            return appendFilter(1, 'Category', 'unitType.utCategory', appendFilter(0, 'Installed', 'installed', []));
        }

        return appendFilter(1, 'Category', 'unitType.utCategory', [])
    }

    const scanNetwork = () => {
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: isTlsEnabled ? MQTTHardwareSubCmds.MQTT_Discovery : HardwareSubCmds.Discover_Controllers,
            userLoginId: user.id,
            jsonResponse: ""
        }

        //Reset previously selected device
        setSelectedDevice({
            id: 0,
            name: '',
            autoCreateDoorsType: TYPE_ENTRY_ONLY,
            autoCreateDoors: true,
            selectedPeripherals: [],
            selectIPDevices: [],
        });
        setIsNextEnabled(false);

        setBusy(true);
        executeCommand(command)
            .then(() => {
                dataRefresh((listParameters) => getDiscoveries({ ...listParameters, filters: getFilters() }));
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
            .finally(() => {
                setBusy(false);
            })
    }

    const handleBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (onBack) {
            onBack(e);
        }
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        onNext(e);
    };

    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const tableLegenItems: TableLegendItem[] = [
        {
            value: '0',
            imageIcon: DiscoveredIcon,
            label: translate.byKey(TranslationKeys.discovered),
            tooltip: translate.byKey(TranslationKeys.discovered)
        },
        {
            value: '1',
            imageIcon: InstalledIcon,
            label: translate.byKey(TranslationKeys.installed),
            tooltip: translate.byKey(TranslationKeys.installed)
        }
    ]

    const checkAdvancedScanOptions = (advancedScanOption: string) => {
        if (advancedScanOption === commands.ADD_RANGE) {
            setOpenAddRangeModal(true);
        }
        else if (advancedScanOption === commands.SELECT_RANGE) {
            setOpenIPRangeSelection(true);
        }
        else if (advancedScanOption === commands.SPECIFIC_IP) {
            setOpenIPSearch(true);
        }
    }

    const advancedScanIpRange = (values) => {
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: HardwareSubCmds.Discover_Controllers,
            userLoginId: user.id,
            jsonResponse: "",
            startIp: values.startIp,
            endIp: values.endIp
        }

        setBusy(true);
        executeCommand(command)
            .then(() => {
                dataRefresh((listParameters) => getDiscoveries({ ...listParameters, filters: getFilters() }));
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
            .finally(() => {
                setBusy(false);
            })
    }

    const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {

        //Reset previously selected device
        setSelectedDevice({
            id: 0,
            name: '',
            macAddress: '',
            autoCreateDoorsType: TYPE_ENTRY_ONLY,
            autoCreateDoors: true,
            selectedPeripherals: [],
            selectIPDevices: [],
        });

        onNext(e);
    };

    return <div className="pt-3 pr-3 h-full relative">
        <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.discoveryMessage)} className={`flex ${busy ? 'h-full w-full justify-center' : ''} flex-wrap content-center`}>
            <div className='flex w-full' style={{ maxHeight: '410px' }}>
                <TableContextProvider
                    useFilter
                    usePagination
                    useTable
                    useSort
                    useSearch
                    dataMethod={(listParameters) => getDiscoveries({ ...listParameters, filters: getFilters() })}
                    tableDescriptor={columns}
                    searchDescriptor={searchColumns}
                    manualDataRefresh={manualDataRefresh}
                    useLegend={showLegend}
                    legendItems={showLegend ? tableLegenItems : []}
                    canExportReport={false}>
                    <TableTab
                        title={title}
                        selectRows={false}
                        onRowClicked={onRowClicked}
                        cyName='selectController' />
                </TableContextProvider>
            </div>
            <div className="flex justify-between w-full absolute bottom-0 pr-3 pb-3">
                {isTlsEnabled ?
                    <button
                        style={{ textOverflow: 'ellipsis' }}
                        className={`py-1 px-4 mr-2 mt-3 rounded-full text-white w-20 h-10 bg-sidemenu-focus text-sm items-center`}
                        type="button"
                        onClick={handleAdd}>
                        <Tooltip text={translate.byKey(TranslationKeys.add)} direction='top' show={true}>
                            <span className="inline-block truncate">{translate.byKey(TranslationKeys.add)}</span>
                        </Tooltip>
                    </button>
                    :
                    <div className="flex flex-row">
                        <button
                            style={{ textOverflow: 'ellipsis' }}
                            className={`py-1 px-4 mr-2 mt-3 rounded-full text-white w-32 h-10 bg-sidemenu-focus text-sm items-center`}
                            type="button"
                            onClick={scanNetwork}>
                            <Tooltip text={translate.byKey(TranslationKeys.scanSubnet)} direction='top' show={true}>
                                <span className="inline-block truncate">{translate.byKey(TranslationKeys.scanSubnet)}</span>
                            </Tooltip>
                        </button>

                        <DropdownSelector className={`py-1 px-4 mr-2 mt-3 rounded-full w-40 h-10 text-white bg-sidemenu-focus text-sm items-center`}
                            onSelected={(event) => checkAdvancedScanOptions(event.value)}
                            direction='top'
                            options={advanceScanOptions}>
                            {translate.byKey(TranslationKeys.advancedScan)}
                        </DropdownSelector>
                    </div>
                }

                <div>
                    <button
                        className={`secondary secondary-inverse mr-2 py-1 px-5 ${onBack ? '' : 'invisible'}`}
                        type="button"
                        onClick={handleBack}>{translate.byKey(TranslationKeys.back)}</button>
                    <button
                        className={`py-1 px-5 mt-3 secondary  ${isNextEnabled ? 'text-white bg-portal-blue-primary-shade2' : 'secondary-disabled cursor-not-allowed'}`}
                        type="button"
                        disabled={!isNextEnabled}
                        onClick={handleNext}>{translate.byKey(TranslationKeys.next)}</button>
                </div>
            </div>
            <AddIpRangeModal isOpen={openAddRangeModal} onclose={() => setOpenAddRangeModal(false)} />
            <IPRanges isOpen={openIPRangeSelection} onClose={() => { setOpenIPRangeSelection(false) }} handleOnSubmit={(values) => advancedScanIpRange(values)} />
            <SingleIPSearchModal isOpen={openIPSearch} onClose={() => { setOpenIPSearch(false) }} handleOnSubmit={(values) => advancedScanIpRange(values)} />
        </ActivityFrame>
    </div>
}

export default SelectController;
